import { config } from './config';
import type {
  AssistantRequest, AssistantResponse, BookRequest, BookResponse, ClientBooking, Slot, Storefront,
} from '../../supabase/functions/_shared/domain/api.ts';
import { ERROR_TEXT } from '../../supabase/functions/_shared/domain/api.ts';

export class ApiError extends Error {
  constructor(public code: string, public status: number) {
    super(ERROR_TEXT[code] ?? ERROR_TEXT.internal);
  }
}

async function request<T>(fn: string, path: string, init: RequestInit & { timeoutMs?: number; jwt?: string } = {}): Promise<T> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), init.timeoutMs ?? 15_000);
  let res: Response;
  try {
    res = await fetch(`${config.functionsUrl}/${fn}/${path}`, {
      ...init,
      signal: ctrl.signal,
      headers: {
        'Content-Type': 'application/json',
        apikey: config.supabaseAnonKey,
        Authorization: `Bearer ${init.jwt ?? config.supabaseAnonKey}`,
        ...(init.headers ?? {}),
      },
    });
  } catch {
    throw new ApiError('network', 0);
  } finally {
    clearTimeout(timer);
  }
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError((body as { error?: string }).error ?? 'internal', res.status);
  return body as T;
}

const post = <T>(path: string, body: unknown) => request<T>('public-api', path, { method: 'POST', body: JSON.stringify(body) });

export const publicApi = {
  storefront: (slug: string) => request<Storefront>('public-api', `storefront?slug=${encodeURIComponent(slug)}`, { method: 'GET' }),
  slots: (slug: string, serviceId: string, from: string, days = 7) =>
    post<{ slots: Slot[] }>('slots', { slug, service_id: serviceId, from, days }).then((r) => r.slots),
  book: (req: BookRequest) => post<BookResponse>('book', req),
  booking: (slug: string, token: string) => post<{ booking: ClientBooking }>('booking', { slug, token }).then((r) => r.booking),
  cancel: (slug: string, token: string, reason?: string) =>
    post<{ booking: ClientBooking }>('cancel', { slug, token, reason }).then((r) => r.booking),
  pushSubscribe: (slug: string, token: string, subscription: PushSubscriptionJSON) =>
    post<{ subscribed: boolean; delivery: 'real' | 'preview_suppressed'; reminder_scheduled: boolean }>('push-subscribe', { slug, token, subscription }),
  assistant: (req: AssistantRequest, jwt?: string) =>
    request<AssistantResponse>('assistant', '', { method: 'POST', body: JSON.stringify(req), jwt, timeoutMs: 40_000 }),
};

export function imageUrl(sf: Pick<Storefront, 'meta'>, path: string | null | undefined): string | undefined {
  if (!path) return undefined;
  if (/^https?:\/\//.test(path)) return path;
  return sf.meta.image_base_url + path.split('/').map(encodeURIComponent).join('/');
}

export type { Storefront, Slot, ClientBooking, AssistantResponse };
